import type { Socket } from 'socket.io-client';

/**
 * Le canal temps réel, exposé aux écrans qui en ont besoin.
 *
 * Le fournisseur (`providers/realtime.provider.tsx`) ouvre le socket et le
 * pose ici ; les écrans ne le manipulent jamais eux-mêmes, ils s'abonnent à un
 * événement et se désabonnent en partant. C'est ce qui permet à un direct de
 * recevoir son tchat et son produit épinglé **avec leur contenu**, là où le
 * reste de l'application ne reçoit que des signaux d'invalidation.
 *
 * Un abonnement posé avant que le socket existe, ou après une reconnexion,
 * tient quand même : les écouteurs sont rejoués sur chaque socket posé.
 */
type Handler = (payload: never) => void;

interface Subscription {
  event: string;
  handler: Handler;
}

let socket: Socket | null = null;
const subscriptions = new Set<Subscription>();
const connectHandlers = new Set<() => void>();

/** Le fournisseur pose le socket ouvert, et `null` en le fermant. */
export function setRealtimeSocket(next: Socket | null): void {
  if (socket) {
    for (const subscription of subscriptions) {
      socket.off(subscription.event, subscription.handler as never);
    }
    for (const handler of connectHandlers) {
      socket.off('connect', handler);
    }
  }
  socket = next;
  if (socket) {
    for (const subscription of subscriptions) {
      socket.on(subscription.event, subscription.handler as never);
    }
    for (const handler of connectHandlers) {
      socket.on('connect', handler);
    }
  }
}

/** S'abonne à un événement du canal. Rend la fonction qui désabonne. */
export function onRealtimeEvent<T>(event: string, handler: (payload: T) => void): () => void {
  const subscription: Subscription = { event, handler: handler as Handler };
  subscriptions.add(subscription);
  socket?.on(event, handler as never);
  return () => {
    subscriptions.delete(subscription);
    socket?.off(event, handler as never);
  };
}

/**
 * Prévenu à chaque (re)connexion du canal. Un socket neuf ne connaît aucune
 * pièce : un direct doit se rejoindre à nouveau pour recevoir sa pièce.
 */
export function onRealtimeConnect(handler: () => void): () => void {
  connectHandlers.add(handler);
  socket?.on('connect', handler);
  return () => {
    connectHandlers.delete(handler);
    socket?.off('connect', handler);
  };
}

export function isRealtimeConnected(): boolean {
  return socket?.connected ?? false;
}
